import { UcoastEl } from '@/scripts/core/UcoastEl'

interface GorgiasQuestion {
	question: string
	answer: string
}

interface GorgiasQuestionsResponse {
	questions?: GorgiasQuestion[]
}

const allowedTags = new Set([
	'A',
	'BR',
	'EM',
	'H1',
	'H2',
	'H3',
	'H4',
	'H5',
	'H6',
	'LI',
	'OL',
	'P',
	'SPAN',
	'STRONG',
	'UL',
])

export class GorgiasHelpCenter extends UcoastEl {
	static htmlSelector = 'gorgias-help-center'

	private fallback?: HTMLElement
	private list?: HTMLElement
	private status?: HTMLElement
	private questionsUrl?: string
	private hasRenderedFallback = false

	constructor() {
		super()

		this.fallback = this.querySelector('[data-gorgias-fallback]') ?? undefined
		this.list = this.querySelector('[data-gorgias-fallback-list]') ?? undefined
		this.status = this.querySelector('[data-gorgias-fallback-status]') ?? undefined
		this.questionsUrl = this.dataset.questionsUrl
	}

	override connectedCallback() {
		super.connectedCallback()

		window.setTimeout(() => this.loadFallbackIfBlocked(), 2500)
		window.setTimeout(() => this.loadFallbackIfBlocked(true), 6000)
	}

	private isEmbedBlocked(forceWhenMissing = false) {
		const iframe = this.querySelector('iframe')

		if (!iframe) return forceWhenMissing

		const attrSrc = iframe.getAttribute('src') ?? ''
		const propertySrc = iframe.src ?? ''
		const blockerHeldSrc =
			attrSrc === 'about:blank' && propertySrc.includes('gorgias')

		if (blockerHeldSrc) return true

		try {
			const body = iframe.contentDocument?.body
			return attrSrc === 'about:blank' && (!body || body.childElementCount === 0)
		} catch {
			return false
		}
	}

	private async loadFallbackIfBlocked(forceWhenMissing = false) {
		if (this.hasRenderedFallback || !this.isEmbedBlocked(forceWhenMissing)) return

		this.classList.add('is-fallback-active')
		this.showFallback()

		if (!this.questionsUrl || !this.list) {
			this.setStatus('Open the full help center for support articles.')
			return
		}

		try {
			const response = await fetch(this.questionsUrl)
			if (!response.ok) throw new Error(`Gorgias request failed: ${response.status}`)

			const data = (await response.json()) as GorgiasQuestionsResponse
			const questions = data.questions ?? []

			if (!questions.length) {
				this.setStatus('Open the full help center for support articles.')
				return
			}

			this.renderQuestions(questions)
			this.setStatus('')
			this.hasRenderedFallback = true
		} catch (error) {
			console.error(error)
			this.setStatus('Open the full help center for support articles.')
		}
	}

	private showFallback() {
		if (!this.fallback) return

		this.fallback.hidden = false
	}

	private setStatus(message: string) {
		if (!this.status) return

		this.status.textContent = message
		this.status.hidden = message.length === 0
	}

	private renderQuestions(questions: GorgiasQuestion[]) {
		if (!this.list) return

		const fragment = document.createDocumentFragment()

		questions.forEach((question) => {
			const details = document.createElement('details')
			const summary = document.createElement('summary')
			const answer = document.createElement('div')

			details.className = 'gorgias-help-center__fallback-item'
			summary.className = 'gorgias-help-center__fallback-question'
			answer.className = 'gorgias-help-center__fallback-answer rte'

			summary.textContent = question.question
			answer.innerHTML = this.sanitizeAnswer(question.answer)

			details.append(summary, answer)
			fragment.append(details)
		})

		this.list.replaceChildren(fragment)
	}

	private sanitizeAnswer(answer: string) {
		const document = new DOMParser().parseFromString(answer, 'text/html')

		document.body.querySelectorAll('*').forEach((element) => {
			if (!allowedTags.has(element.tagName)) {
				element.replaceWith(...Array.from(element.childNodes))
				return
			}

			Array.from(element.attributes).forEach((attribute) => {
				if (
					element.tagName === 'A' &&
					['href', 'target', 'rel'].includes(attribute.name)
				) {
					return
				}

				element.removeAttribute(attribute.name)
			})

			if (element.tagName === 'A') {
				const href = element.getAttribute('href') ?? ''
				if (!this.isSafeHref(href)) element.removeAttribute('href')

				element.setAttribute('target', '_blank')
				element.setAttribute('rel', 'noopener')
			}
		})

		return document.body.innerHTML
	}

	private isSafeHref(href: string) {
		try {
			const url = new URL(href, window.location.origin)
			return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)
		} catch {
			return false
		}
	}
}
